/**
 * <home-frame-customizer> — the frame customizer, preview-only, for the homepage.
 *
 * Two frames share one frame colour: a plain-wall frame on the left, and the
 * same frame placed in a room on the right (a fixed size, in the room at true
 * scale) which the visitor can drag to reposition. No upload — a sample image
 * stands in for the customer's photo.
 *
 * Same frame component as the product page (assets/gallery-wall.css; sizes and
 * colours from assets/photo-frame-sizes.json; rooms from gallery-wall-rooms.json).
 *
 * Markup + styles: sections/home-frame-customizer.liquid.
 */
if (!customElements.get('home-frame-customizer')) {
  const SHEENS = {
    dark: 'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 38%, rgba(0,0,0,0.28) 100%)',
    light: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(0,0,0,0.04) 55%, rgba(0,0,0,0.10) 100%)',
    none: 'none',
  };

  customElements.define(
    'home-frame-customizer',
    class HomeFrameCustomizer extends HTMLElement {
      connectedCallback() {
        this.wall = this.querySelector('[data-wall]');
        this.roomWall = this.querySelector('[data-room-wall]');
        this.roomStage = this.querySelector('[data-room-stage]');
        this.colorPicker = this.querySelector('[data-colors]');
        this.meta = this.querySelector('[data-meta]');

        this.sample = this.dataset.sample || '';
        this.size = this.dataset.defaultSize || 'XL';
        this.color = this.dataset.defaultColor || 'Black';
        this.roomKey = this.dataset.room || 'background-3';
        this.offset = { x: 0, y: 0 };
        this.roomBase = null;
        this.dragState = null;

        this.addEventListener('click', (event) => {
          const color = event.target.closest('[data-color]');
          if (color) {
            this.color = color.dataset.color;
            this.render();
          }
        });

        for (const type of ['pointerdown', 'pointermove', 'pointerup', 'pointercancel']) {
          this.roomStage?.addEventListener(type, (event) => this.onPointer(type, event));
        }

        this.load();
      }

      async load() {
        try {
          const [sizes, rooms] = await Promise.all([
            fetch(this.dataset.sizes).then((r) => r.json()),
            fetch(this.dataset.rooms).then((r) => r.json()),
          ]);
          this.sizes = sizes;
          this.rooms = rooms;
          this.room = rooms[this.roomKey];
        } catch (error) {
          console.error('home-frame-customizer: could not load data', error);
          return;
        }
        if (!this.sizes || !this.room) return;
        this.renderPickers();
        this.render();
      }

      sizeDef() {
        return (
          this.sizes.sizes.find((s) => s.label === this.size) ||
          this.sizes.sizes[this.sizes.sizes.length - 1]
        );
      }
      colorDef() {
        return this.sizes.colors[this.color] || this.sizes.colors.Black;
      }
      outer() {
        const s = this.sizeDef();
        return { w: s.art.w + 2 * s.mould, h: s.art.h + 2 * s.mould, mould: s.mould, art: s.art };
      }

      renderPickers() {
        if (!this.colorPicker) return;
        const colors = Object.keys(this.sizes.colors).filter((c) => c !== 'Only Print');
        this.colorPicker.innerHTML = colors
          .map(
            (c) =>
              `<button type="button" class="hfc-chip" data-color="${c}">
                 <span class="hfc-swatch" style="--swatch: ${this.sizes.colors[c].frame || 'transparent'}"></span>${c}
               </button>`
          )
          .join('');
      }

      render() {
        this.colorPicker?.querySelectorAll('[data-color]').forEach((b) =>
          b.classList.toggle('is-active', b.dataset.color === this.color)
        );
        this.paintFrame(this.wall);
        this.paintFrame(this.roomWall);
        if (this.meta) this.meta.textContent = `${this.size} · ${this.outer().art.w} × ${this.outer().art.h} in`;
        this.placeRoom();
      }

      /** Draw the frame (same size + colour) into a `.gallery-wall` element. */
      paintFrame(el) {
        if (!el) return;
        const { w, h, mould } = this.outer();
        const color = this.colorDef();
        const onlyPrint = !color.frame;
        el.style.setProperty('--gw-w', w);
        el.style.setProperty('--gw-h', h);
        el.style.setProperty('--frame-border', onlyPrint ? 0 : mould);
        el.style.setProperty('--mat', 0);
        el.style.setProperty('--matboard', 0);
        el.style.setProperty('--frame-color', color.frame || 'transparent');
        el.style.setProperty('--mat-color', color.frame || 'transparent');
        el.style.setProperty('--frame-sheen', SHEENS[color.sheen] || 'none');
        el.classList.add('is-no-mat');
        el.innerHTML = `
          <figure class="frame" style="--x:0; --y:0; --w:${w}; --h:${h}; --mat:0">
            <div class="frame__mat"><div class="frame__art" data-art="0"></div></div>
          </figure>`;
        const artEl = el.querySelector('[data-art="0"]');
        if (artEl && this.sample) {
          artEl.style.backgroundImage = `url('${this.sample}')`;
          artEl.classList.add('is-filled');
          el.querySelector('.frame')?.classList.add('is-filled');
        }
      }

      /** Place the frame in the room at true scale — same maths as the product page. */
      placeRoom() {
        const room = this.room;
        if (!room || !this.roomStage) return;
        const { w, h } = this.outer();
        const ppi = room.reference.px / room.reference.inches;
        const wallPxW = w * ppi;
        const wallPxH = h * ppi;
        const left = room.anchor.centerX - wallPxW / 2;
        const topMarginPx = (room.topMargin ?? 6) * ppi;
        const gapPx = (room.gap ?? 8) * ppi;
        let top = room.anchor.sofaBackTopY - gapPx - wallPxH;
        if (top < topMarginPx) top = topMarginPx;

        this.roomStage.style.setProperty('--room-image', `url('${room.image}')`);
        this.roomStage.style.setProperty('--room-ratio', `${room.size.w} / ${room.size.h}`);
        this.roomStage.style.setProperty('--room-width', `${((wallPxW / room.size.w) * 100).toFixed(3)}%`);

        this.roomBase = {
          leftPct: (left / room.size.w) * 100,
          topPct: (top / room.size.h) * 100,
          widthPct: (wallPxW / room.size.w) * 100,
          heightPct: (wallPxH / room.size.h) * 100,
        };
        this.applyRoomPlacement();
      }

      applyRoomPlacement() {
        const b = this.roomBase;
        if (!b) return;
        const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
        const x = clamp(b.leftPct + this.offset.x, 0, 100 - b.widthPct);
        const y = clamp(b.topPct + this.offset.y, 0, 100 - b.heightPct);
        this.roomStage.style.setProperty('--room-left', `${x.toFixed(3)}%`);
        this.roomStage.style.setProperty('--room-top', `${y.toFixed(3)}%`);
      }

      /* ---- drag the frame around the room (preview only) ------------------ */

      onPointer(type, event) {
        if (type === 'pointerdown') return this.dragStart(event);
        if (type === 'pointermove') return this.dragMove(event);
        return this.dragEnd(event);
      }

      dragStart(event) {
        if (event.button !== 0 || !this.roomBase) return;
        const rect = this.roomStage.getBoundingClientRect();
        this.dragState = {
          id: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          offX: this.offset.x,
          offY: this.offset.y,
          w: rect.width,
          h: rect.height,
        };
        this.roomStage.setPointerCapture?.(event.pointerId);
        this.roomStage.classList.add('is-dragging');
        event.preventDefault();
      }

      dragMove(event) {
        const d = this.dragState;
        if (!d || event.pointerId !== d.id) return;
        this.offset.x = d.offX + ((event.clientX - d.startX) / d.w) * 100;
        this.offset.y = d.offY + ((event.clientY - d.startY) / d.h) * 100;
        this.applyRoomPlacement();
      }

      dragEnd(event) {
        const d = this.dragState;
        if (!d || event.pointerId !== d.id) return;
        this.dragState = null;
        this.roomStage.classList.remove('is-dragging');
      }
    }
  );
}
